document.addEventListener("DOMContentLoaded", async function () {
  const loading = document.getElementById("property-loading");
  const content = document.getElementById("property-content");

  if (!loading || !content) {
    console.error("Property details containers not found");
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const caseNumber = params.get("case_number");

  if (!caseNumber) {
    loading.innerHTML = "Missing property case number.";
    return;
  }

  try {
    const result = await fetchPropertyDetails(caseNumber);
    window.currentProperty = result;

    if (!result.success) {
      loading.innerHTML = "Property not found.";
      return;
    }

    content.innerHTML = `
      ${renderHero(result.property)}
      ${renderProvaloIntelligence(result)}
      ${renderPropertyTimeline(result)}
      ${renderSection(
        "📍 Property Location",
        `
            <div id="property-map" class="property-map"></div>
        `
    )}
      ${renderOverview(result.property)}
      ${renderTaxes(result.taxes)}
      ${renderViolations(result.violations)}
      ${renderMortgages(result.mortgages)}
      ${renderDeeds(result.deeds, result.property)}
      ${renderLiens(result.liens)}
      ${renderDocuments(result.documents, result.property)}
    `;

    loading.style.display = "none";
    content.style.display = "block";
    renderMap(result.property);

  } catch (error) {
    console.error(error);
    loading.innerHTML = "Error loading property details.";
  }
});

async function fetchPropertyDetails(caseNumber) {
  const url =
    "https://api.goprovalo.com/property_details_api.php" +
    `?case_number=${encodeURIComponent(caseNumber)}`;

  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/json"
    }
  });

  const responseText = await response.text();
  let result;

  try {
    result = JSON.parse(responseText);
  } catch (error) {
    console.error(
      "Invalid property details response:",
      responseText
    );

    throw new Error(
      "The property service returned an invalid response."
    );
  }

  if (!response.ok || !result.success) {
    throw new Error(
      result.message ||
      result.error ||
      "Unable to load the property."
    );
  }

  return result;
}

function renderHero(property) {
  const image =
    `https://api.goprovalo.com/streetview.php?lat=${property.lat}&lon=${property.lon}`;

  return `
    <section class="property-hero-premium">

      <div class="hero-image-panel">
        <img
          src="${image}"
          alt="${property.address}"
          loading="lazy"
          onerror="this.src='https://placehold.co/900x500?text=Street+View+Unavailable';"
        >
      </div>

      <div class="hero-info-panel">

        <span class="hero-status ${getDetailStatusClass(property.status)}">
          ${getDetailStatusLabel(property.status)}
        </span>

        <h1>${property.address}</h1>

        <p class="hero-location">
          ${property.city}, FL ${property.zip}
        </p>

        <div class="hero-market-value">
          <span>${property.assessed_value || "N/A"}</span>
          <small>Estimated Market Value</small>
        </div>

        <div class="hero-specs">
          <span>🛏 ${property.beds || "N/A"} Beds</span>
          <span>🛁 ${property.baths || "N/A"} Baths</span>
          <span>📅 Built ${property.year_built || "N/A"}</span>
          <span>📐 Lot ${property.lot_size || "N/A"}</span>
        </div>

        <div class="hero-auction-box">
          <strong>Auction Date</strong>
          <span>${formatDetailDate(property.sale_date)}</span>
        </div>

        <div class="hero-actions">
          <button 
            class="hero-btn secondary detail-save-btn ${PropertyWishlist.isSaved(property.case_number) ? "saved" : ""}"
            data-case="${property.case_number}">
            ${PropertyWishlist.isSaved(property.case_number) ? "♥ Saved" : "♡ Save"}
          </button>
          <button class="hero-btn primary">⬇ Report</button>
          <button
            type="button"
            class="hero-btn secondary detail-share-btn"
            data-case="${property.case_number}"
            aria-label="Share ${property.address}"
          >
            ↗ Share
          </button>
          <button class="hero-btn secondary back-catalog-btn">
            ← Back
          </button>
        </div>

      </div>

    </section>
  `;
}

function renderPropertyTimeline(result) {
  const property = result.property || {};
  const mortgages = result.mortgages || [];
  const deeds = result.deeds || [];
  const liens = result.liens || [];
  const violations = result.violations || [];

  const milestones = buildTimelineMilestones(
    property,
    mortgages,
    deeds,
    liens,
    violations
  );

  return renderSection(
    "📅 Property Timeline",
    `
      <div class="timeline-summary">
        <div>
          <strong>${property.year_built || "N/A"}</strong>
          <span>Year Built</span>
        </div>

        <div>
          <strong>${deeds.length}</strong>
          <span>Ownership Transfers</span>
        </div>

        <div>
          <strong>${mortgages.length}</strong>
          <span>Recorded Mortgages</span>
        </div>

        <div>
          <strong>${property.status || "N/A"}</strong>
          <span>Current Status</span>
        </div>
      </div>

      <div class="property-timeline milestone-timeline">
        ${milestones.map(item => `
          <div class="timeline-item ${item.type}">
            <div class="timeline-dot"></div>

            <div class="timeline-content">
              <span>${item.date}</span>
              <h3>${item.title}</h3>
              <p>${item.description}</p>
            </div>
          </div>
        `).join("")}
      </div>

      <div class="timeline-insight">
        <strong>Timeline Insight</strong>
        <p>${generateMilestoneInsight(property, mortgages, deeds, liens, violations)}</p>
      </div>
    `
  );
}

function buildTimelineMilestones(property, mortgages, deeds, liens, violations) {
  const milestones = [];

  if (property.year_built) {
    milestones.push({
      date: property.year_built,
      title: "Property Built",
      description: "Original construction year according to property records.",
      type: "historic"
    });
  }

  const deedYears = deeds
    .map(deed => extractYearFromText(deed.details))
    .filter(Boolean)
    .sort();

  if (deedYears.length > 0) {
    milestones.push({
      date: `${deedYears[0]} → ${deedYears[deedYears.length - 1]}`,
      title: `${deeds.length} Ownership Transfer${deeds.length === 1 ? "" : "s"}`,
      description: getOwnershipSummary(deeds),
      type: "ownership"
    });
  }

  const mortgageYears = mortgages
    .map(mortgage => extractYearFromText(mortgage.recorder_date || mortgage.record))
    .filter(Boolean)
    .sort();

  if (mortgageYears.length > 0) {
    milestones.push({
      date: `${mortgageYears[0]} → ${mortgageYears[mortgageYears.length - 1]}`,
      title: `${mortgages.length} Recorded Mortgage${mortgages.length === 1 ? "" : "s"}`,
      description: getMortgageSummary(mortgages),
      type: "financing"
    });
  }

  if (liens.length > 0) {
    milestones.push({
      date: "Recorded",
      title: `${liens.length} Lien Record${liens.length === 1 ? "" : "s"}`,
      description: "Lien records were found and should be reviewed before acquisition.",
      type: "foreclosure"
    });
  }

  if (violations.length > 0) {
    milestones.push({
      date: "Municipal",
      title: `${violations.length} Code Violation${violations.length === 1 ? "" : "s"}`,
      description: "Municipal/code enforcement records were found for this property.",
      type: "municipal"
    });
  }

  if (property.final_judgment && parseMoney(property.final_judgment) > 0) {
    milestones.push({
      date: "Judgment",
      title: "Final Judgment Recorded",
      description: formatMoney(parseMoney(property.final_judgment)),
      type: "foreclosure"
    });
  }

  if (property.sale_date) {
    milestones.push({
      date: formatPropertyDate(property.sale_date),
      title: "Auction Scheduled",
      description: getAuctionTimelineDescription(property.sale_date, property.status),
      type: "auction"
    });
  }

  return milestones;
}

function getOwnershipSummary(deeds) {
  if (!deeds || deeds.length === 0) {
    return "No ownership transfer records were found.";
  }

  const latest = deeds[0];

  if (latest.grantee) {
    return `Latest recorded grantee: ${latest.grantee}.`;
  }

  return "Ownership transfer records were found in public records.";
}

function getMortgageSummary(mortgages) {
  if (!mortgages || mortgages.length === 0) {
    return "No recorded mortgage activity was found.";
  }

  const openMortgages = mortgages.filter(m =>
    String(m.mor_open || "").toLowerCase() === "true"
  );

  if (openMortgages.length > 0) {
    return `${openMortgages.length} mortgage record${openMortgages.length === 1 ? "" : "s"} appear open and should be reviewed.`;
  }

  return "Recorded mortgage activity was found; review payoff and satisfaction history.";
}

function getAuctionTimelineDescription(saleDate, status) {
  const countdown = getAuctionCountdown(saleDate);

  if (countdown.label === "Auction Passed") {
    return `Auction date has passed. Current status: ${status || "N/A"}.`;
  }

  return `${countdown.label}. Current status: ${status || "N/A"}.`;
}

function generateMilestoneInsight(property, mortgages, deeds, liens, violations) {
  if (liens.length > 0 || violations.length > 0) {
    return "This timeline shows municipal or lien activity that should be reviewed before making an investment decision.";
  }

  if (mortgages.length > 3) {
    return "The property shows multiple recorded mortgages, indicating possible financing or title complexity. A payoff and satisfaction review is recommended.";
  }

  if (deeds.length > 4) {
    return "The property has several ownership transfers. Investors should review the title chain before acquisition.";
  }

  if (property.status === "Accepting Proxy") {
    return "The timeline indicates active auction activity. Investors should prioritize document review and bidding strategy before the sale date.";
  }

  return "The property timeline summarizes the major ownership, financing, foreclosure, and auction milestones available in public records.";
}

function extractYearFromText(value) {
  if (!value) return null;

  const match = String(value).match(/\b(19|20)\d{2}\b/);

  return match ? match[0] : null;
}

function getDetailStatusLabel(status) {
  const labels = {
    "Accepting Proxy": "Active Auction",
    "Sold": "Sold",
    "Canceled": "Canceled",
    "Pre ForeClosure": "Pre-Foreclosure"
  };

  return labels[status] || status || "Unknown";
}

function renderMap(property) {
  const mapContainer = document.getElementById("property-map");

  if (!mapContainer || !property.lat || !property.lon) {
    return;
  }

  const lat = property.lat;
  const lon = property.lon;

  mapContainer.innerHTML = `
    <iframe
      width="100%"
      height="100%"
      style="border:0;"
      loading="lazy"
      allowfullscreen
      referrerpolicy="no-referrer-when-downgrade"
      src="https://www.google.com/maps?q=${lat},${lon}&z=16&output=embed">
    </iframe>
  `;
}

function getDetailStatusClass(status) {
  if (!status) return "status-default";

  const normalized = status.toLowerCase();

  if (normalized.includes("accepting")) return "status-accepting";
  if (normalized.includes("pre")) return "status-pre";
  if (normalized.includes("sold")) return "status-sold";
  if (normalized.includes("cancel")) return "status-canceled";

  return "status-default";
}

function formatDetailDate(dateValue) {
  if (!dateValue) return "N/A";

  const date = new Date(dateValue + "T00:00:00");

  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function renderQuickStats(property) {
  return `
    <section class="quick-stats">
      ${renderStat("Beds", property.beds)}
      ${renderStat("Baths", property.baths)}
      ${renderStat("Year Built", property.year_built)}
      ${renderStat("Lot Size", property.lot_size)}
      ${renderStat("Market Value", property.assessed_value)}
    </section>
  `;
}

function renderStat(label, value) {
  return `
    <div class="quick-stat-card">
      <span>${value || "N/A"}</span>
      <strong>${label}</strong>
    </div>
  `;
}

function renderSection(title, body) {
  return `
    <section class="dashboard-section">
      <h2>${title}</h2>
      ${body}
    </section>
  `;
}

function renderEmptyMessage(message) {
  return `
    <div class="empty-positive">
      ${message}
    </div>
  `;
}

function renderOverview(property) {
  const finalJudgment =
    property.final_judgment !== null &&
    property.final_judgment !== undefined &&
    property.final_judgment !== ""
      ? formatMoney(
          parseMoney(property.final_judgment)
        )
      : "N/A";

  return `
    ${renderQuickStats(property)}

    <section class="dashboard-section">
      <h2>Property Information</h2>

      <div class="dashboard-grid">
        ${renderField(
          "Case Number",
          property.case_number
        )}

        ${renderField(
          "Parcel",
          property.parcel
        )}

        ${renderField(
          "Plaintiff",
          property.certificate_holder
        )}

        ${renderField(
          "Final Judgment",
          finalJudgment
        )}

        ${renderField(
          "Owner",
          property.owner,
          "dashboard-item-wide"
        )}
      </div>
    </section>
  `;
}

function renderTaxes(taxes = {}) {
  const balance =
    taxes.balance !== null &&
    taxes.balance !== undefined &&
    taxes.balance !== ""
      ? formatMoney(parseMoney(taxes.balance))
      : "N/A";

  const assessedValue =
    taxes.assessed_value !== null &&
    taxes.assessed_value !== undefined &&
    taxes.assessed_value !== ""
      ? formatMoney(
          parseMoney(taxes.assessed_value)
        )
      : "N/A";

  return renderSection(
    "Taxes",
    `
      <div class="dashboard-grid">
        ${renderField(
          "Tax Balance",
          balance
        )}

        ${renderField(
          "Assessed Value",
          assessedValue
        )}

        ${renderField(
          "Property Owner",
          taxes.owner
        )}

        ${renderField(
          "Last Updated",
          taxes.last_update
        )}

        ${renderField(
          "Owner Address",
          taxes.owner_address,
          "dashboard-item-wide"
        )}
      </div>
    `
  );
}

function renderProvaloIntelligence(result) {
  const property = result.property || {};
  const taxes = result.taxes || {};
  const mortgages = result.mortgages || [];
  const liens = result.liens || [];
  const violations = result.violations || [];

  const score = calculateInvestmentScore(property, taxes, mortgages, liens, violations);
  const rating = getInvestmentRating(score);
  const risk = calculateRiskLevel(mortgages, liens, violations, taxes);
  const strategy = getRecommendedStrategy(property, taxes, mortgages, liens, violations);
  const countdown = getAuctionCountdown(property.sale_date);
  const equity = estimateEquity(property, taxes);
  const confidence = calculateConfidence(property, taxes, mortgages, violations);
  const summary = generateInvestorSummary(property, taxes, mortgages, liens, violations, risk, strategy);

  return renderSection(
    "🧠 Provalo Intelligence",
    `
      <div class="intelligence-panel enhanced">

        <div class="intelligence-main-score">
          <span>${score}</span>
          <small>/ 100</small>
          <strong>Investment Rating</strong>
          <p>${rating}</p>

          <div class="score-bar">
            <div class="score-fill" style="width:${score}%"></div>
          </div>
        </div>

        <div class="intelligence-grid">

          <div class="intelligence-card">
            <label>Auction</label>
            <strong>${countdown.label}</strong>
            <small>${countdown.date}</small>
          </div>

          <div class="intelligence-card">
            <label>Risk Level</label>
            <strong class="${getRiskClass(risk)}">${getRiskIcon(risk)} ${risk}</strong>
          </div>

          <div class="intelligence-card">
            <label>Recommended Strategy</label>
            <strong>${strategy}</strong>
            <small>${getStrategyReason(strategy)}</small>
          </div>

          <div class="intelligence-card">
            <label>Estimated Equity</label>
            <strong>${equity}</strong>
          </div>

          <div class="intelligence-card">
            <label>Data Confidence</label>
            <strong>${confidence}%</strong>
            <div class="mini-bar">
              <div class="mini-fill" style="width:${confidence}%"></div>
            </div>
          </div>

          <div class="intelligence-card summary-card">
            <label>Investor Summary</label>
            <p>${summary}</p>
          </div>

        </div>
      </div>
    `
  );
}

function buildTimelineEvents(property, mortgages, deeds) {
  let events = [];

  if (property.year_built) {
    events.push({
      date: property.year_built,
      title: "Property Built",
      description: "Original year built according to property records.",
      type: "historic"
    });
  }

  deeds.forEach(deed => {
    const year = extractYear(deed.details);

    if (year) {
      events.push({
        date: year,
        title: "Ownership Transfer",
        description: `${deed.grantor || "Grantor"} → ${deed.grantee || "Grantee"}`,
        type: "ownership"
      });
    }
  });

  mortgages.forEach(mortgage => {
    const year = extractYear(mortgage.recorder_date);

    if (year) {
      events.push({
        date: year,
        title: "Mortgage Recorded",
        description: `${mortgage.party_1 || "Borrower"} / ${mortgage.party_2 || "Lender"}`,
        type: "financing"
      });
    }
  });

  if (property.final_judgment && parseMoney(property.final_judgment) > 0) {
    events.push({
      date: "Judgment",
      title: "Final Judgment Recorded",
      description: formatMoney(parseMoney(property.final_judgment)),
      type: "foreclosure"
    });
  }

  if (property.sale_date) {
    events.push({
      date: formatPropertyDate(property.sale_date),
      title: "Auction Scheduled",
      description: property.status || "Auction activity detected.",
      type: "auction"
    });
  }

  return events.sort((a, b) => getTimelineSortValue(a.date) - getTimelineSortValue(b.date));
}

function extractYear(value) {
  if (!value) return null;

  const match = String(value).match(/\b(19|20)\d{2}\b/);

  return match ? match[0] : null;
}

function getTimelineSortValue(value) {
  if (!value) return 9999;

  const year = extractYear(value);

  if (year) return Number(year);

  if (String(value).includes("Judgment")) return 9998;

  return 9999;
}

function generateTimelineInsight(property, mortgages, deeds) {
  if (deeds.length <= 2 && mortgages.length <= 2) {
    return "The property shows a relatively simple ownership and financing history based on available public records.";
  }

  if (mortgages.length > 3) {
    return "Multiple recorded mortgages indicate additional financing complexity. Investors should review payoff history and recorded mortgage documents carefully.";
  }

  if (deeds.length > 4) {
    return "Several ownership transfers were found, which may require additional title review before acquisition.";
  }

  if (property.status === "Accepting Proxy") {
    return "The property is currently associated with active auction activity, making timing and document review especially important.";
  }

  return "Timeline records provide a quick view of ownership, financing, and foreclosure activity associated with this property.";
}

function formatPropertyDate(dateValue) {
  if (!dateValue) return "N/A";

  const date = new Date(String(dateValue).replace(" ", "T"));

  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function calculateInvestmentScore(property, taxes, mortgages, liens, violations) {
  let score = 50;

  if (property.status === "Accepting Proxy") score += 20;
  if (property.sale_date) score += 10;
  if (!liens || liens.length === 0) score += 10;
  if (!violations || violations.length === 0) score += 10;
  if (taxes && Number(taxes.balance || 0) === 0) score += 10;
  if (mortgages && mortgages.length <= 2) score += 5;

  return Math.min(100, score);
}

function getInvestmentRating(score) {
  if (score >= 85) return "Excellent Opportunity";
  if (score >= 70) return "Strong Opportunity";
  if (score >= 55) return "Moderate Opportunity";
  return "High Risk";
}

function calculateRiskLevel(mortgages, liens, violations, taxes) {
  let risk = 0;

  if (mortgages && mortgages.length > 3) risk += 2;
  if (liens && liens.length > 0) risk += 2;
  if (violations && violations.length > 0) risk += 1;
  if (taxes && Number(taxes.balance || 0) > 0) risk += 2;

  if (risk >= 5) return "High";
  if (risk >= 3) return "Medium";
  return "Low";
}

function getRecommendedStrategy(property, taxes, mortgages, liens, violations) {
  if (liens && liens.length > 0) return "Review Before Bidding";
  if (violations && violations.length > 2) return "Municipal Risk Review";
  if (property.status === "Accepting Proxy") return "Auction Purchase";
  if (property.status === "Pre ForeClosure") return "Short Sale / Outreach";
  return "Monitor";
}

function getAuctionCountdown(saleDate) {
  if (!saleDate) {
    return {
      label: "N/A",
      date: "No auction date available"
    };
  }

  const today = new Date();
  const auction = new Date(saleDate);
  const diff = Math.ceil((auction - today) / (1000 * 60 * 60 * 24));

  const formattedDate = auction.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });

  if (diff < 0) {
    return {
      label: "Auction Passed",
      date: formattedDate
    };
  }

  if (diff === 0) {
    return {
      label: "🔴 Auction Today",
      date: formattedDate
    };
  }

  if (diff === 1) {
    return {
      label: "🟠 Auction Tomorrow",
      date: formattedDate
    };
  }

  if (diff <= 7) {
    return {
      label: `🔥 ${diff} Days Remaining`,
      date: formattedDate
    };
  }

  return {
    label: `${diff} Days Remaining`,
    date: formattedDate
  };
}

function estimateEquity(property, taxes) {
  const value = parseMoney(property.assessed_value);
  const judgment = parseMoney(property.final_judgment);
  const taxBalance = parseMoney(taxes?.balance);

  if (!value || !judgment) return "N/A";

  const equity = value - judgment - taxBalance;

  return formatMoney(equity);
}

function calculateConfidence(property, taxes, mortgages, violations) {
  let points = 0;
  let total = 8;

  if (property.address) points++;
  if (property.sale_date) points++;
  if (property.lat && property.lon) points++;
  if (property.beds) points++;
  if (property.baths) points++;
  if (property.owner) points++;
  if (taxes) points++;
  if (mortgages !== undefined || violations !== undefined) points++;

  return Math.round((points / total) * 100);
}

function parseMoney(value) {
  if (!value) return 0;

  return Number(
    String(value)
      .replace(/[^0-9.-]/g, "")
  ) || 0;
}

function formatMoney(value) {
  if (value < 0) return "$0";

  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}

function renderMortgages(mortgages) {
  if (
    !Array.isArray(mortgages) ||
    mortgages.length === 0
  ) {
    return renderEmptySection(
      "Mortgage History"
    );
  }

  const getMortgageTimestamp = (value) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return null;
    }

    const dateValue = String(value).trim();

    /*
     * YYYY-MM-DD
     */
    let match = dateValue.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})/
    );

    if (match) {
      return Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
    }

    /*
     * MM/DD/YYYY or MM-DD-YYYY
     */
    match = dateValue.match(
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/
    );

    if (match) {
      return Date.UTC(
        Number(match[3]),
        Number(match[1]) - 1,
        Number(match[2])
      );
    }

    const timestamp = Date.parse(dateValue);

    return Number.isNaN(timestamp)
      ? null
      : timestamp;
  };

  /*
   * Copy before sorting so the original mortgage
   * collection remains unchanged.
   */
  const sortedMortgages = [...mortgages].sort(
    (firstMortgage, secondMortgage) => {
      const firstDate =
        getMortgageTimestamp(
          firstMortgage.recorder_date
        );

      const secondDate =
        getMortgageTimestamp(
          secondMortgage.recorder_date
        );

      if (
        firstDate === null &&
        secondDate === null
      ) {
        return 0;
      }

      if (firstDate === null) {
        return 1;
      }

      if (secondDate === null) {
        return -1;
      }

      return secondDate - firstDate;
    }
  );

  return renderSection(
    "🏦 Mortgage History",
    `
      <p class="section-subtitle">
        Current and historical mortgages recorded for this property.
      </p>

      ${renderMortgageSummary(mortgages)}

      <div class="details-table-wrap">
        <table class="details-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Borrower</th>
              <th>Lender</th>
              <th>Recording</th>
              <th>Clerk File</th>
              <th>Document</th>
            </tr>
          </thead>

          <tbody>
            ${sortedMortgages.map((mortgage) => {
              const isOpen =
                String(
                  mortgage.mor_open
                ).toLowerCase() === "true" ||
                String(
                  mortgage.mor_open
                ) === "1";

              return `
                <tr>
                  <td>
                    ${mortgage.recorder_date || ""}
                  </td>

                  <td>
                    <span class="mortgage-status ${
                      isOpen
                        ? "status-open"
                        : "status-closed"
                    }">
                      ${
                        isOpen
                          ? "Open"
                          : "Satisfied"
                      }
                    </span>
                  </td>

                  <td>
                    ${formatMoney(
                      mortgage.mor_amount
                    )}
                  </td>

                  <td>
                    ${mortgage.party_1 || ""}
                  </td>

                  <td>
                    ${mortgage.party_2 || ""}
                  </td>

                  <td>
                    ${mortgage.recbp || ""}
                  </td>

                  <td>
                    ${mortgage.cfnd || ""}
                  </td>

                  <td>
                    ${
                      mortgage.link
                        ? `
                          <a
                            class="table-link"
                            href="${mortgage.link}"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        `
                        : ""
                    }
                  </td>
                </tr>
              `;
            }).join("")}
          </tbody>
        </table>
      </div>
    `
  );
}

function renderMortgageSummary(mortgages) {
  let openCount = 0;
  let closedCount = 0;
  let totalAmount = 0;

  mortgages.forEach(m => {
    const isOpen =
      String(m.mor_open).toLowerCase() === "true" ||
      String(m.mor_open) === "1";

    if (isOpen) {
      openCount++;
    } else {
      closedCount++;
    }

    const amount = Number(
      String(m.mor_amount || "").replace(/[^0-9.-]/g, "")
    );

    if (!Number.isNaN(amount)) {
      totalAmount += amount;
    }
  });

  return `
    <div class="mortgage-summary-grid">
      <div class="mortgage-summary-card">
        <span>Total Mortgages</span>
        <strong>${mortgages.length}</strong>
      </div>

      <div class="mortgage-summary-card">
        <span>Open</span>
        <strong>${openCount}</strong>
      </div>

      <div class="mortgage-summary-card">
        <span>Satisfied</span>
        <strong>${closedCount}</strong>
      </div>

      <div class="mortgage-summary-card">
        <span>Total Amount</span>
        <strong>${formatMoney(totalAmount)}</strong>
      </div>
    </div>
  `;
}

function renderDeeds(deeds) {
  if (
    !Array.isArray(deeds) ||
    deeds.length === 0
  ) {
    return renderEmptySection(
      "Property Ownership History"
    );
  }

  /*
   * Extract only a year located at the beginning
   * of the Details field.
   *
   * Examples:
   * "2025 Warranty Deed" -> 2025
   * " 2021 Quit Claim"   -> 2021
   */
  const getLeadingDetailsYear = (details) => {
    if (
      details === null ||
      details === undefined
    ) {
      return null;
    }

    const match = String(details).match(
      /^\s*((?:19|20)\d{2})\b/
    );

    return match
      ? Number(match[1])
      : null;
  };

  /*
   * Copy the collection before sorting so the original
   * API data remains unchanged.
   */
  const sortedDeeds = deeds
    .map((deed, originalIndex) => ({
      deed,
      originalIndex,
      year: getLeadingDetailsYear(
        deed.details
      )
    }))
    .sort((firstRecord, secondRecord) => {
      if (
        firstRecord.year === null &&
        secondRecord.year === null
      ) {
        return (
          firstRecord.originalIndex -
          secondRecord.originalIndex
        );
      }

      if (firstRecord.year === null) {
        return 1;
      }

      if (secondRecord.year === null) {
        return -1;
      }

      const yearDifference =
        secondRecord.year -
        firstRecord.year;

      if (yearDifference !== 0) {
        return yearDifference;
      }

      return (
        firstRecord.originalIndex -
        secondRecord.originalIndex
      );
    })
    .map((record) => record.deed);

  return renderSection(
    "🏠 Property Ownership History",
    `
      <p class="section-subtitle">
        Historical ownership transfers recorded by
        ${currentPropertyCounty()}.
      </p>

      <div class="details-table-wrap">
        <table class="details-table">
          <thead>
            <tr>
              <th>Recording</th>
              <th>Grantor</th>
              <th>Grantee</th>
              <th>Details</th>
            </tr>
          </thead>

          <tbody>
            ${sortedDeeds.map((deed) => `
              <tr>
                <td>
                  ${deed.book_page || ""}
                </td>

                <td>
                  ${deed.grantor || ""}
                </td>

                <td>
                  ${deed.grantee || ""}
                </td>

                <td>
                  ${deed.details || ""}
                </td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `
  );
}
function formatViolationAmount(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return "N/A";
  }

  const originalValue = String(value).trim();

  const numericValue = Number(
    originalValue.replace(/[$,\s]/g, "")
  );

  /*
   * Preserve nonmonetary text instead of incorrectly
   * converting values such as N/A into $0.
   */
  if (!Number.isFinite(numericValue)) {
    return originalValue;
  }

  return numericValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function renderViolations(violations) {
  if (!violations || violations.length === 0) {
    return renderSection(
      "🚨 Code Violations",
      `
        <p class="section-subtitle">
          Open or historical code enforcement violations associated with this property.
        </p>

        <div class="empty-positive">
          ✔ No code violation records were found.
        </div>
      `
    );
  }

  return renderSection(
    "🚨 Code Violations",
    `
      <p class="section-subtitle">
        Open or historical code enforcement violations associated with this property.
      </p>

      <div class="details-table-wrap">
        <table class="details-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Citation</th>
              <th>Issue Date</th>
              <th>Amount Due</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${violations.map(v => `
              <tr>
                <td>${v.status || ""}</td>
                <td>${v.citation || ""}</td>
                <td>${v.issue_date || ""}</td>
                <td>${formatViolationAmount(v.due)}</td>
                <td>${v.description || ""}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `
  );
}

function renderDocumentCard(doc, caseNumber) {
  const isAvailable = doc.available === true;

  return `
    <div class="document-card">
      <div>
        <h3>${doc.title}</h3>
        <p>${getDocumentDescription(doc.type)}</p>
      </div>

      <div>
        ${
          isAvailable
            ? `<button 
                class="document-btn"
                data-case-number="${caseNumber}"
                data-document-type="${doc.type}">
                Download PDF
              </button>`
            : `<button class="document-btn disabled" disabled>
                Not Available
              </button>`
        }
      </div>
    </div>
  `;
}

function renderDocuments(documents, property) {
  const docs = documents || [];
  const caseNumber = property?.case_number || "";

  const premiumReport = docs.find(doc => doc.type === "property_report");
  const supportingDocs = docs.filter(doc => doc.type !== "property_report");

  return renderSection(
    "📁 Due Diligence Documents",
    `
      <p class="section-subtitle">
        Download available due diligence reports and supporting documents for this property.
      </p>

      ${renderPremiumReportCard(premiumReport, caseNumber)}

      <h3 class="supporting-documents-title">Supporting Documents</h3>

      ${
        supportingDocs.length === 0
          ? `<div class="empty-positive">No supporting documents are currently available for this case.</div>`
          : supportingDocs.map(doc => renderDocumentCard(doc, caseNumber)).join("")
      }
    `
  );
}

function renderPremiumReportCard(report, caseNumber) {
  return `
    <div class="premium-report-card">

      <div class="premium-header">
        <div>
          <h2>Provalo Investor Report</h2>
          <p>
            Professional investment report prepared from property, tax,
            court, lien, deed, mortgage, violation, and document records.
          </p>
        </div>

        <div class="premium-badge">PREMIUM</div>
      </div>

      <div class="premium-features">
        <span>✓ Executive Summary</span>
        <span>✓ Investment Score</span>
        <span>✓ Financial Snapshot</span>
        <span>✓ Mortgage Analysis</span>
        <span>✓ Liens & Taxes</span>
        <span>✓ Violations</span>
        <span>✓ Ownership Timeline</span>
        <span>✓ Supporting Documents Index</span>
      </div>

      <button
        class="download-premium-report document-btn"
        data-case-number="${caseNumber}"
        data-document-type="property_report">
        Download Investor Report
      </button>

    </div>
  `;
}

function generateInvestorSummary(property, taxes, mortgages, liens, violations, risk, strategy) {
  const status = property.status || "";
  const taxBalance = parseMoney(taxes?.balance);

  if (risk === "High") {
    return "This property presents elevated risk due to liens, violations, taxes, or title complexity. Investors should complete additional due diligence before bidding or negotiating.";
  }

  if (status === "Accepting Proxy" && taxBalance === 0 && (!violations || violations.length === 0)) {
    return "This property appears to be an active auction opportunity with limited municipal exposure and no visible tax delinquency. It may be suitable for investors seeking near-term acquisition opportunities.";
  }

  if (status === "Pre ForeClosure") {
    return "This property is in pre-foreclosure status and may be better suited for outreach, short sale negotiation, or early-stage investor review before auction activity increases.";
  }

  if (mortgages && mortgages.length > 3) {
    return "Multiple recorded mortgages suggest additional title complexity. Investors should review payoff history and recorded documents before making an acquisition decision.";
  }

  return `This property shows a ${risk.toLowerCase()} risk profile based on the available public records. Recommended strategy: ${strategy}.`;
}

function getRiskIcon(risk) {
  if (risk === "High") return "🔴";
  if (risk === "Medium") return "🟡";
  return "🟢";
}

function getRiskClass(risk) {
  if (risk === "High") return "risk-high";
  if (risk === "Medium") return "risk-medium";
  return "risk-low";
}

function getStrategyReason(strategy) {
  const reasons = {
    "Auction Purchase": "Auction activity detected",
    "Short Sale / Outreach": "Pre-foreclosure opportunity",
    "Review Before Bidding": "Additional lien review recommended",
    "Municipal Risk Review": "Violation exposure detected",
    "Monitor": "No immediate action required"
  };

  return reasons[strategy] || "Based on current property records";
}

function cleanDocumentTitle(title) {
  if (!title) return "Document";

  return title
    .replace(/^\d{4}-\d{6}-CA-\d{2}_?/i, "")
    .replace(/_/g, " ")
    .trim();
}

function currentPropertyCounty() {
  return window.currentProperty?.property?.county || "the county";
}

function renderLiens(liens) {
  if (
    !Array.isArray(liens) ||
    liens.length === 0
  ) {
    return renderSection(
      "⚖️ Outstanding Liens",
      `
        <p class="section-subtitle">
          Recorded lien information associated with this property.
        </p>

        <div class="empty-positive">
          ✔ No outstanding lien records were found.
        </div>
      `
    );
  }

  const getLienDateTimestamp = (value) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return null;
    }

    const dateValue = String(value).trim();

    /*
     * YYYY-MM-DD
     */
    let match = dateValue.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})/
    );

    if (match) {
      return Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
    }

    /*
     * MM/DD/YYYY or MM-DD-YYYY
     */
    match = dateValue.match(
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/
    );

    if (match) {
      return Date.UTC(
        Number(match[3]),
        Number(match[1]) - 1,
        Number(match[2])
      );
    }

    /*
     * Fall back to a four-digit year located
     * anywhere in the Recording Date value.
     */
    match = dateValue.match(
      /\b((?:19|20)\d{2})\b/
    );

    if (match) {
      return Date.UTC(
        Number(match[1]),
        0,
        1
      );
    }

    const timestamp = Date.parse(dateValue);

    return Number.isNaN(timestamp)
      ? null
      : timestamp;
  };

  /*
   * Copy the collection before sorting.
   */
  const sortedLiens = liens
    .map((lien, originalIndex) => ({
      lien,
      originalIndex,
      timestamp: getLienDateTimestamp(
        lien.rec_date
      )
    }))
    .sort((firstRecord, secondRecord) => {
      if (
        firstRecord.timestamp === null &&
        secondRecord.timestamp === null
      ) {
        return (
          firstRecord.originalIndex -
          secondRecord.originalIndex
        );
      }

      if (firstRecord.timestamp === null) {
        return 1;
      }

      if (secondRecord.timestamp === null) {
        return -1;
      }

      const dateDifference =
        secondRecord.timestamp -
        firstRecord.timestamp;

      if (dateDifference !== 0) {
        return dateDifference;
      }

      return (
        firstRecord.originalIndex -
        secondRecord.originalIndex
      );
    })
    .map((record) => record.lien);

  return renderSection(
    "⚖️ Outstanding Liens",
    `
      <p class="section-subtitle">
        Recorded lien information associated with this property.
      </p>

      <div class="details-table-wrap">
        <table class="details-table">
          <thead>
            <tr>
              <th>Clerk File</th>
              <th>Type</th>
              <th>Recording Date</th>
              <th>Recording</th>
              <th>First Party</th>
              <th>Second Party</th>
            </tr>
          </thead>

          <tbody>
            ${sortedLiens.map((lien) => `
              <tr>
                <td>
                  ${lien.clerk_file_no || ""}
                </td>

                <td>
                  ${lien.doc_type || ""}
                </td>

                <td>
                  ${lien.rec_date || ""}
                </td>

                <td>
                  ${lien.rec_book_page || ""}
                </td>

                <td>
                  ${lien.first_party || ""}
                </td>

                <td>
                  ${lien.second_party || ""}
                </td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `
  );
}

function renderField(label, value, extraClass = "") {
  return `
    <div class="dashboard-item ${extraClass}">
      <strong>${label}</strong>
      <span>${value || "N/A"}</span>
    </div>
  `;
}

function renderLinkField(label, url) {

    return `
        <div class="dashboard-item">

            <strong>${label}</strong>

            ${
                url
                ? `<a class="dashboard-link" href="${url}" target="_blank">View Tax Record</a>`
                : "N/A"
            }

        </div>
    `;

}

function getDocumentDescription(type) {
  const descriptions = {
    appraiser: "County property appraiser report.",
    complaint: "Foreclosure complaint document.",
    lispendens: "Recorded Lis Pendens document.",
    tax: "Property tax document.",
    property_report: "Complete due diligence report."
  };

  return descriptions[type] || "Due diligence document.";
}

function isMortgageOpen(value) {
  return String(value || "").toLowerCase() === "true" || String(value || "") === "1";
}

function formatMoney(value) {

    if (!value)
        return "N/A";

    const number = Number(
        String(value).replace(/[^0-9.-]/g, "")
    );

    if (Number.isNaN(number))
        return value;

    return number.toLocaleString(
        "en-US",
        {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0
        }
    );

}

document.addEventListener("click", function (event) {
  const button = event.target.closest(".premium-report-unavailable");

  if (!button) return;

  showMessage(
    "Provalo Due Diligence Report",
    "This property's premium report is currently being generated. Supporting documents remain available."
  );
});

document.addEventListener(
  "click",
  async function (event) {
    const button = event.target.closest(
      ".detail-save-btn"
    );

    if (!button || button.disabled) {
      return;
    }

    event.preventDefault();

    const caseNumber = button.dataset.case;

    if (!caseNumber) {
      console.error(
        "The property case number is missing."
      );

      return;
    }

    const previousText = button.textContent;

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const isSaved =
        await PropertyWishlist.toggle(
          caseNumber
        );

      button.classList.toggle(
        "saved",
        isSaved
      );

      button.textContent = isSaved
        ? "♥ Saved"
        : "♡ Save";

      button.setAttribute(
        "aria-label",
        isSaved
          ? "Remove saved property"
          : "Save property"
      );
    } catch (error) {
      console.error(
        "Property wishlist update failed:",
        error
      );

      button.textContent = previousText;

      showMessage(
        "Unable to update saved property",
        error.message ||
          "Please try again later."
      );
    } finally {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }
);

document.addEventListener("click", function (event) {
  const button = event.target.closest(".back-catalog-btn");

  if (!button) return;

  window.location.href = "/pages/properties";
});

document.addEventListener(
  "click",
  async function (event) {
    const button = event.target.closest(
      ".detail-share-btn"
    );

    if (!button || button.disabled) {
      return;
    }

    event.preventDefault();

    const caseNumber = button.dataset.case;
    const property =
      window.currentProperty?.property || {};

    if (!caseNumber) {
      showMessage(
        "Unable to share property",
        "The property case number is missing."
      );

      return;
    }

    const propertyUrl =
      "https://www.goprovalo.com/pages/property-details" +
      `?case_number=${encodeURIComponent(caseNumber)}`;

    const propertyAddress =
      property.address || "GoProvalo property";

    const shareData = {
      title: `${propertyAddress} | GoProvalo`,
      text:
        `Review this property opportunity on GoProvalo: ` +
        propertyAddress,
      url: propertyUrl
    };

    /*
     * Use the native share sheet when supported.
     */
    if (typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
      } catch (error) {
        /*
         * Closing the Share sheet is not an error
         * that needs to be displayed.
         */
        if (error.name !== "AbortError") {
          console.error(
            "Native property sharing failed:",
            error
          );

          await copyPropertyLink(
            propertyUrl,
            button
          );
        }
      }

      return;
    }

    /*
     * Desktop fallback.
     */
    await copyPropertyLink(
      propertyUrl,
      button
    );
  }
);

async function copyPropertyLink(
  propertyUrl,
  button
) {
  const originalText = button.textContent;

  try {
    if (
      navigator.clipboard &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(
        propertyUrl
      );
    } else {
      const temporaryInput =
        document.createElement("textarea");

      temporaryInput.value = propertyUrl;
      temporaryInput.setAttribute(
        "readonly",
        ""
      );

      temporaryInput.style.position = "fixed";
      temporaryInput.style.opacity = "0";
      temporaryInput.style.pointerEvents = "none";

      document.body.appendChild(
        temporaryInput
      );

      temporaryInput.select();
      temporaryInput.setSelectionRange(
        0,
        temporaryInput.value.length
      );

      const copied =
        document.execCommand("copy");

      temporaryInput.remove();

      if (!copied) {
        throw new Error(
          "The browser rejected the copy request."
        );
      }
    }

    button.textContent = "✓ Link Copied";
    button.setAttribute(
      "aria-label",
      "Property link copied"
    );

    window.setTimeout(() => {
      button.textContent = originalText;
      button.setAttribute(
        "aria-label",
        "Share property"
      );
    }, 2200);
  } catch (error) {
    console.error(
      "Unable to copy property link:",
      error
    );

    showMessage(
      "Unable to copy link",
      "Please copy the property URL from your browser."
    );
  }
}

document.addEventListener("click", async function (event) {
  const button = event.target.closest(".document-btn");

  if (!button || button.disabled) {
    return;
  }

  const caseNumber = button.dataset.caseNumber;
  const documentType = button.dataset.documentType;

  if (!caseNumber || !documentType) {
    console.error("Missing case number or document type.");

    showMessage(
      "Document Error",
      "The document information is incomplete."
    );

    return;
  }

  const csrfToken = PropertyWishlist.getCsrfToken();

  if (!csrfToken) {
    showMessage(
      "Login Required",
      "Please sign in before downloading documents.",
      "login"
    );

    return;
  }

  const apiBaseUrl = "https://api.goprovalo.com";

  const checkUrl =
    `${apiBaseUrl}/check_download_access.php` +
    `?case_number=${encodeURIComponent(caseNumber)}` +
    `&type=${encodeURIComponent(documentType)}`;

  /*
   * Open a blank tab during the original click.
   * This prevents Safari and mobile browsers from blocking it.
   */
  const documentWindow = window.open("about:blank", "_blank");

  if (!documentWindow) {
    showMessage(
      "Popup Blocked",
      "Please allow popups for GoProvalo and try again."
    );

    return;
  }

  documentWindow.opener = null;

  button.disabled = true;
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");

  try {
    const response = await fetch(checkUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({})
    });

    const responseText = await response.text();

    let result;

    try {
      result = JSON.parse(responseText);
    } catch (jsonError) {
      console.error(
        "Invalid JSON returned by check_download_access.php:",
        responseText
      );

      documentWindow.close();

      showMessage(
        "Connection Error",
        "The server returned an invalid response. Please try again later."
      );

      return;
    }

    if (!response.ok || !result.success || !result.allowed) {
      documentWindow.close();

      showMessage(
        result.title || "Unable to open document",
        result.message || "Please try again later.",
        result.action || null
      );

      return;
    }

    const finalDocumentUrl = result.download_url;

    if (
      !finalDocumentUrl ||
      typeof finalDocumentUrl !== "string"
    ) {
      documentWindow.close();

      showMessage(
        "Document Error",
        "The server did not return a valid document link."
      );

      return;
    }

    let parsedDocumentUrl;

    try {
      parsedDocumentUrl = new URL(finalDocumentUrl);
    } catch (urlError) {
      console.error(
        "Invalid document URL:",
        finalDocumentUrl,
        urlError
      );

      documentWindow.close();

      showMessage(
        "Document Error",
        "The document link returned by the server is invalid."
      );

      return;
    }

    if (parsedDocumentUrl.origin !== apiBaseUrl) {
      console.error(
        "Unexpected document origin:",
        parsedDocumentUrl.origin
      );

      documentWindow.close();

      showMessage(
        "Document Error",
        "The document link returned by the server is not authorized."
      );

      return;
    }

    documentWindow.location.replace(finalDocumentUrl);
  } catch (error) {
    console.error(
      "Document access verification failed:",
      error
    );

    documentWindow.close();

    showMessage(
      "Connection Error",
      "Unable to verify document access. Please try again later."
    );
  } finally {
    button.disabled = false;
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
  }
});

function renderEmptySection(title, message = "No records were found for this property.") {
  return renderSection(
    title,
    renderEmptyMessage(`✔ ${message}`)
  );
}

function showMessage(title, message, action = null) {
  document.getElementById("message-title").textContent = title;
  document.getElementById("message-text").textContent = message;

  const upgradeBtn = document.getElementById("message-upgrade");

  if (action === "upgrade") {
    upgradeBtn.classList.remove("hidden");
  } else {
    upgradeBtn.classList.add("hidden");
  }

  document.getElementById("message-modal").classList.remove("hidden");
}

document
.getElementById("message-close")
.addEventListener("click", function(){

    document
        .getElementById("message-modal")
        .classList.add("hidden");

});

document
.getElementById("message-upgrade")
.addEventListener("click", function () {

    window.location.href = "/pages/subscription-plans";

});